/** Main dashboard page with summary cards, server list, and business KPIs. */
import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Container,
  Grid,
  Button,
  Alert,
  CircularProgress,
  Snackbar,
  AlertColor,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { serversApi, businessKpisApi } from "../api/client";
import { Server, ServerStatus } from "../types";
import {
  BusinessKPIs,
  ProviderAlert,
  Period,
  KPITab,
} from "../types/kpi";
import ServerList from "../components/servers/ServerList";
import { TypedServerCard } from "../components/servers/ServerCard";
import AddServerModal from "../components/servers/AddServerModal";
import BusinessKPICard from "../components/dashboard/BusinessKPICard";
import KPITabs from "../components/dashboard/KPITabs";
import QualityAlertBanner from "../components/dashboard/QualityAlertBanner";
import ConfirmDialog from "../components/common/ConfirmDialog";
import { useAutoRefresh } from "../hooks/useAutoRefresh";
import PageHeader from "../components/common/PageHeader";
import { useNotify } from "../components/common/NotificationProvider";

/** Auto-refresh interval in milliseconds (30 seconds). */
const AUTO_REFRESH_INTERVAL = 30000;

/** KPI auto-refresh interval in milliseconds (5 minutes). */
const KPI_REFRESH_INTERVAL = 300000;

/** Period options for KPI filtering. */
const PERIOD_OPTIONS: { value: Period; label: string }[] = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "last_7_days", label: "Last 7 Days" },
  { value: "last_30_days", label: "Last 30 Days" },
  { value: "this_month", label: "This Month" },
  { value: "last_month", label: "Last Month" },
];

/** Main dashboard page component. */
export default function Dashboard() {
  const navigate = useNavigate();
  const [servers, setServers] = useState<Server[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [removeServer, setRemoveServer] = useState<Server | null>(null);
  const [removing, setRemoving] = useState(false);
  const notify = useNotify();

  // Business KPIs state
  const [kpis, setKpis] = useState<BusinessKPIs | null>(null);
  const [kpiLoading, setKpiLoading] = useState(true);
  const [kpiError, setKpiError] = useState<string | null>(null);
  const [period, setPeriod] = useState<Period>("today");
  const [activeTab, setActiveTab] = useState<KPITab>("financial");
  const [alerts, setAlerts] = useState<ProviderAlert[]>([]);

  /** Fetch servers from API. */
  const fetchServers = useCallback(async () => {
    try {
      setError(null);
      const response = await serversApi.list();
      setServers(response.data);
    } catch (err: any) {
      console.error("Failed to fetch servers:", err);
      if (err.response?.status === 401) {
        // Auth error will be handled by interceptor
        return;
      }
      setError(err.response?.data?.detail || "Failed to fetch servers");
    } finally {
      setLoading(false);
    }
  }, []);

  /** Calculate summary statistics from servers. */
  const stats = React.useMemo(() => {
    return {
      total: servers.length,
      online: servers.filter((s) => s.status === ServerStatus.ONLINE).length,
      degraded: servers.filter((s) => s.status === ServerStatus.DEGRADED).length,
      totalCC: servers.reduce((sum, s) => sum + (s.active_calls || 0), 0),
      totalCPS: servers.reduce(
        (sum, s) => sum + (s.cpu_percent !== null ? 1 : 0),
        0
      ), // Simplified - should use actual CPS from metrics
    };
  }, [servers]);

  /** Show notification message (F4: global toasts). */
  const showNotification = (message: string, severity: "success" | "error" | "info" | "warning" = "success") => {
    notify(message, severity);
  };

  /** Handle server view action. */
  const handleViewServer = (server: Server) => {
    navigate(`/servers/${server.id}`);
  };

  /** Handle server remove request (confirmed via dialog — UX review C2). */
  const handleRemoveServerRequest = useCallback(
    (serverId: string) => {
      setRemoveServer(servers.find((s) => s.id === serverId) || null);
    },
    [servers]
  );

  const handleRemoveServerConfirmed = async () => {
    if (!removeServer) return;
    setRemoving(true);
    try {
      await serversApi.remove(removeServer.id);
      showNotification("Server marked for removal (draining)");
      setRemoveServer(null);
      await fetchServers();
    } catch (err: any) {
      showNotification(
        err.response?.data?.detail || "Failed to remove server",
        "error"
      );
    } finally {
      setRemoving(false);
    }
  };

  /** Handle download join script action. */
  const handleDownloadScript = async (serverId: string) => {
    try {
      const response = await serversApi.getJoinScript(serverId);
      const blob = new Blob([response.data], { type: "text/plain" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `join-server-${serverId}.sh`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      showNotification("Join script downloaded successfully");
    } catch (err: any) {
      showNotification(
        err.response?.data?.detail || "Failed to download join script",
        "error"
      );
    }
  };

  /** Handle server created from modal. */
  const handleServerCreated = () => {
    setAddModalOpen(false);
    showNotification("Server created successfully");
    fetchServers();
  };

  /** Fetch KPIs from API. */
  const fetchKPIs = useCallback(async () => {
    try {
      setKpiError(null);
      const response = await businessKpisApi.getKPIs({
        period,
        tab: activeTab,
      });
      setKpis(response.data);
    } catch (err: any) {
      console.error("Failed to fetch KPIs:", err);
      if (err.response?.status === 401) {
        // Auth error will be handled by interceptor
        return;
      }
      setKpiError(err.response?.data?.detail || "Failed to fetch KPIs");
    } finally {
      setKpiLoading(false);
    }
  }, [period, activeTab]);

  /** Fetch provider quality alerts. */
  const fetchAlerts = useCallback(async () => {
    // Provider alerts endpoint not implemented yet - skip
    setAlerts([]);
  }, []);

  /** Handle period change. */
  const handlePeriodChange = (
    _event: React.MouseEvent<HTMLElement>,
    newPeriod: Period | null,
  ) => {
    if (newPeriod !== null) {
      setPeriod(newPeriod);
    }
  };

  /** Handle tab change. */
  const handleTabChange = (tabIndex: number, tabName: string) => {
    setActiveTab(tabName as KPITab);
  };

  /** Format number with commas and decimals. */
  const formatNumber = (value: number | undefined | null, decimals = 2): string => {
    if (value === undefined || value === null) return "0";
    return value.toLocaleString("en-US", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  };

  /** Format percentage value. */
  const formatPercentage = (value: number | undefined | null): string => {
    if (value === undefined || value === null) return "0.0%";
    return `${value.toFixed(1)}%`;
  };

  /** Fetch servers on mount, honoring the LiveIndicator (F3) and pausing
   * while the tab is hidden (J1). */
  useAutoRefresh(fetchServers, AUTO_REFRESH_INTERVAL);

  /** Fetch KPIs periodically with the same auto-refresh semantics. */
  useAutoRefresh(fetchKPIs, KPI_REFRESH_INTERVAL);

  /** Re-fetch KPIs immediately when period or tab changes. */
  useEffect(() => {
    fetchKPIs();
  }, [fetchKPIs]);

  /** Fetch provider alerts on mount. */
  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header (G2: shared PageHeader) */}
      <PageHeader
        title="Server Dashboard"
        subtitle="Monitor and manage your softswitch servers"
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddModalOpen(true)}
            size="large"
          >
            Add Server
          </Button>
        }
      />

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={2.4}>
              <TypedServerCard type="total" value={stats.total} />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <TypedServerCard type="online" value={stats.online} />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <TypedServerCard type="degraded" value={stats.degraded} />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <TypedServerCard type="calls" value={stats.totalCC.toLocaleString()} />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <TypedServerCard type="cps" value={stats.totalCPS.toLocaleString()} />
            </Grid>
          </Grid>

          {/* Business KPIs Section */}
          <Box sx={{ mb: 4 }}>
            {/* KPIs Header with Period Selector */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <Box>
                <Typography variant="h5" component="h2" gutterBottom>
                  Business KPIs
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Track financial, quality, and volume metrics
                </Typography>
              </Box>
              <ToggleButtonGroup
                value={period}
                exclusive
                onChange={handlePeriodChange}
                aria-label="KPI period selector"
                size="small"
              >
                {PERIOD_OPTIONS.map((option) => (
                  <ToggleButton key={option.value} value={option.value}>
                    {option.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>

            {/* KPI Error Alert */}
            {kpiError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setKpiError(null)}>
                {kpiError}
              </Alert>
            )}

            {/* Quality Alert Banner */}
            {!kpiLoading && alerts.length > 0 && (
              <QualityAlertBanner providers={alerts} />
            )}

            {/* KPIs Tabs with Cards */}
            {!kpiLoading && kpis ? (
              <KPITabs
                financialContent={
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={4}>
                      <BusinessKPICard
                        title="Revenue"
                        value={`$${formatNumber(kpis?.financial?.revenue?.value)}`}
                        trend={kpis?.financial?.revenue?.trend}
                        period={PERIOD_OPTIONS.find((p) => p.value === period)?.label}
                        color="success"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <BusinessKPICard
                        title="Profit"
                        value={`$${formatNumber(kpis?.financial?.profit?.value)}`}
                        trend={kpis?.financial?.profit?.trend}
                        period={PERIOD_OPTIONS.find((p) => p.value === period)?.label}
                        color="primary"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <BusinessKPICard
                        title="Cost"
                        value={`$${formatNumber(kpis?.financial?.cost?.value)}`}
                        trend={kpis?.financial?.cost?.trend}
                        period={PERIOD_OPTIONS.find((p) => p.value === period)?.label}
                        color="warning"
                      />
                    </Grid>
                  </Grid>
                }
                qualityContent={
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={3}>
                      <BusinessKPICard
                        title="ASR"
                        value={formatPercentage(kpis?.quality?.asr?.value)}
                        trend={kpis?.quality?.asr?.trend}
                        period={PERIOD_OPTIONS.find((p) => p.value === period)?.label}
                        color="info"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <BusinessKPICard
                        title="ACD"
                        value={`${(kpis?.quality?.acd?.value || 0).toFixed(0)}s`}
                        trend={kpis?.quality?.acd?.trend}
                        period={PERIOD_OPTIONS.find((p) => p.value === period)?.label}
                        color="info"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <BusinessKPICard
                        title="PDD"
                        value={`${(kpis?.quality?.pdd?.value || 0).toFixed(2)}s`}
                        trend={kpis?.quality?.pdd?.trend}
                        period={PERIOD_OPTIONS.find((p) => p.value === period)?.label}
                        color="info"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <BusinessKPICard
                        title="NER"
                        value={formatPercentage(kpis?.quality?.ner?.value)}
                        trend={kpis?.quality?.ner?.trend}
                        period={PERIOD_OPTIONS.find((p) => p.value === period)?.label}
                        color="info"
                      />
                    </Grid>
                  </Grid>
                }
                volumeContent={
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={4}>
                      <BusinessKPICard
                        title="Total Calls"
                        value={formatNumber(kpis?.volume?.total_calls?.value, 0)}
                        trend={kpis?.volume?.total_calls?.trend}
                        period={PERIOD_OPTIONS.find((p) => p.value === period)?.label}
                        color="primary"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <BusinessKPICard
                        title="Active Calls"
                        value={formatNumber(kpis?.volume?.active_calls?.value, 0)}
                        trend={kpis?.volume?.active_calls?.trend}
                        period={PERIOD_OPTIONS.find((p) => p.value === period)?.label}
                        color="success"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <BusinessKPICard
                        title="CPS"
                        value={formatNumber(kpis?.volume?.cps?.value, 1)}
                        trend={kpis?.volume?.cps?.trend}
                        period={PERIOD_OPTIONS.find((p) => p.value === period)?.label}
                        color="info"
                      />
                    </Grid>
                  </Grid>
                }
                onTabChange={handleTabChange}
              />
            ) : (
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={4}>
                  <BusinessKPICard
                    title="Revenue"
                    value="--"
                    loading
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <BusinessKPICard
                    title="Profit"
                    value="--"
                    loading
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <BusinessKPICard
                    title="Cost"
                    value="--"
                    loading
                  />
                </Grid>
              </Grid>
            )}
          </Box>

          {/* Server List */}
          <ServerList
            servers={servers}
            onView={handleViewServer}
            onRemove={handleRemoveServerRequest}
            onDownloadScript={handleDownloadScript}
          />
        </>
      )}

      {/* Add Server Modal */}
      <AddServerModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onServerCreated={handleServerCreated}
      />

      {/* Remove server confirmation */}
      <ConfirmDialog
        open={removeServer !== null}
        title="Remove server"
        message={`Remove ${removeServer?.hostname ?? "this server"} from the cluster?`}
        details={
          <>
            The server enters <strong>draining</strong> state: active calls finish, no
            new calls are routed to it.
          </>
        }
        confirmLabel="Remove server"
        severity="warning"
        loading={removing}
        onClose={() => setRemoveServer(null)}
        onConfirm={handleRemoveServerConfirmed}
      />
    </Container>
  );
}
